import type { CandidateDetail } from '@/lib/api/client';
import { EventRiskLabel } from '@/components/EventRiskLabel';

/** Live mid this far from the bot's credit (as a fraction of the bot's credit) is marked as drift. */
export const DRIFT_THRESHOLD = 0.2;

const money = (n: number | null | undefined) => (n == null ? '—' : n.toFixed(2));
const pct = (n: number | null | undefined) => (n == null ? '—' : `${n.toFixed(1)}%`);
const delta = (n: number | null | undefined) => (n == null ? '—' : n.toFixed(3));
const FINISHED: Record<string, string> = { inside: 'inside', below_put: 'below put', above_call: 'above call' };

/** Longest chart read the cell shows before eliding; the rest of the sentence is a tooltip away. */
const CHART_MAX = 120;

/**
 * The bot's chart read in one line: its first sentence, which is the one that says whether price
 * is inside the shorts. The remainder is context for the report, not for a table row — the full
 * text stays on the row as a `title` so nothing is actually hidden.
 */
function chartSummary(chart: string): string {
  const end = chart.indexOf('. ');
  const first = end === -1 ? chart.trim() : chart.slice(0, end + 1).trim();
  return first.length > CHART_MAX ? `${first.slice(0, CHART_MAX).trimEnd()}…` : first;
}

function Outcome({ o }: { o: CandidateDetail['outcome'] }) {
  if (!o) return <span>—</span>;
  if (o.status === 'settled') {
    const cls = (o.theoreticalPnl ?? 0) >= 0 ? 'text-green-700' : 'text-red-700';
    return <span className={cls}>{FINISHED[o.finished ?? '']} · {money(o.theoreticalPnl)} at {money(o.settlementPrice)}</span>;
  }
  if (o.status === 'unavailable') return <span className="text-gray-500">no close captured</span>;
  const touch = o.touchedPut ? ' · touched put' : o.touchedCall ? ' · touched call' : '';
  return <span className="text-gray-600">pending · {o.daysTracked}d tracked{touch}</span>;
}

/**
 * An empty Events cell asserts "the sweep found nothing", which is only true of a symbol the
 * sweep has visited. `CalendarCoverageNotice` says the same thing as a per-symbol banner and
 * does not fit a table of many symbols, so this is the in-row form of it.
 */
function CoverageMarker({ coverage }: { coverage: CandidateDetail['calendarCoverage'] }) {
  if (!coverage || coverage.hasData) return null;
  return <span data-testid="not-swept" className="text-[10px] text-gray-400">calendar not swept</span>;
}

/**
 * Whether Todd actually traded this pick. Four states, and two of them are the reason this is a
 * component rather than a ternary: `linked` with no chain id means a replay regenerated the chain
 * ids out from under the link, which is repairable (Tools → Match bot candidates) and must read as
 * a broken link rather than "never traded"; `cleared` means the user unlinked it by hand and the
 * matcher will never re-apply it, which as an em dash would read as a row nothing has reached yet.
 */
function Traded({ c, onUnlink }: { c: CandidateDetail; onUnlink: (id: string) => void }) {
  if (c.linkState === 'cleared') return <span data-testid="cleared" className="text-gray-400">cleared</span>;
  if (c.linkState !== 'linked') return <span className="text-gray-400">—</span>;
  const clear = (
    <button
      type="button"
      data-testid="unlink"
      className="ml-1 text-gray-400 hover:text-red-600"
      title="Clear this link — the matcher will not re-apply it"
      onClick={() => onUnlink(c.id)}
    >
      ×
    </button>
  );
  if (!c.chainId) {
    return (
      <span data-testid="link-lost" className="text-amber-700">
        link lost after replay{clear}
      </span>
    );
  }
  return (
    <span data-testid="traded">
      {/* Plain anchor, not react-router's Link: this table is tested without a router. */}
      <a href={`/chains/${c.chainId}`} className="text-sky-700 underline">
        {c.matchKind}
      </a>
      {clear}
    </span>
  );
}

export function CandidatesTable({
  candidates,
  onUnlink,
}: {
  candidates: CandidateDetail[];
  onUnlink: (id: string) => void;
}) {
  if (candidates.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="text-left text-xs text-gray-500">
          <tr>
            <th className="py-2 pr-3">#</th><th className="py-2 pr-3">Ticker</th><th className="py-2 pr-3">Exp · DTE</th>
            <th className="py-2 pr-3">Legs (buy P / sell P / sell C / buy C)</th>
            <th className="py-2 pr-3">Bot credit</th><th className="py-2 pr-3">Live mid / natural</th>
            <th className="py-2 pr-3">Deltas bot → live</th><th className="py-2 pr-3">IV rank · tech</th>
            <th className="py-2 pr-3">Events</th><th className="py-2 pr-3">Move · room</th><th className="py-2 pr-3">Flags</th>
            <th className="py-2 pr-3">Traded</th>
            <th className="py-2 pr-3">Chart</th><th className="py-2">Outcome</th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((c) => {
            const drift = c.live?.mid != null && Math.abs(c.live.mid - c.credit) / c.credit > DRIFT_THRESHOLD;
            return (
              <tr key={c.id} data-testid={`candidate-row-${c.id}`} className="border-t border-gray-100 align-top">
                <td className="py-2 pr-3 font-mono">{c.rank ?? '—'}</td>
                <td className="py-2 pr-3 font-semibold">{c.ticker}{c.live?.underlyingPrice != null && <span className="ml-1 font-normal text-gray-500">{money(c.live.underlyingPrice)}</span>}</td>
                <td className="py-2 pr-3 whitespace-nowrap">{c.expiration}{c.dte != null && <span className="text-gray-500"> · {c.dte}d</span>}</td>
                <td className="py-2 pr-3 font-mono whitespace-nowrap" data-testid="legs">{c.longPut} P / {c.shortPut} P / {c.shortCall} C / {c.longCall} C</td>
                <td className="py-2 pr-3 font-mono" data-testid="bot-credit">{money(c.credit)} <span className="text-gray-500">({pct(c.creditPctOfWidth)} of {c.width})</span></td>
                <td className="py-2 pr-3 font-mono" data-testid="live-credit">
                  {c.live ? <>{money(c.live.mid)} / {money(c.live.natural)}{drift && <span data-testid="drift" className="ml-1 rounded bg-amber-100 px-1 text-xs text-amber-800">drift</span>}</> : <span className="text-gray-400">live unavailable</span>}
                </td>
                <td className="py-2 pr-3 font-mono whitespace-nowrap">{delta(c.shortPutDelta)}/{delta(c.shortCallDelta)} → {delta(c.live?.shortPutDelta)}/{delta(c.live?.shortCallDelta)}</td>
                <td className="py-2 pr-3 font-mono">{c.ivRank ?? '—'} · {c.technical ?? '—'}</td>
                <td className="py-2 pr-3"><EventRiskLabel risk={c.calendar} /><CoverageMarker coverage={c.calendarCoverage} />{c.events.length > 0 && <div className="text-xs text-amber-700">{c.events.join('; ')}</div>}</td>
                <td className="py-2 pr-3 font-mono whitespace-nowrap">±{money(c.live?.expectedMove)} · {pct(c.live?.roomPutPct)} / {pct(c.live?.roomCallPct)}</td>
                <td className="py-2 pr-3 text-xs" data-testid="flags">{c.flags.join(', ') || '—'}</td>
                <td className="py-2 pr-3 text-xs whitespace-nowrap"><Traded c={c} onUnlink={onUnlink} /></td>
                <td className="py-2 pr-3 text-xs text-gray-600 min-w-[12rem]" data-testid="chart" title={c.chart ?? undefined}>
                  {c.chart ? chartSummary(c.chart) : '—'}
                </td>
                <td className="py-2" data-testid="outcome"><Outcome o={c.outcome} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
